/**
 * Lock24hBanner — Iter-192.2
 *
 * Sticky, compact banner at the top of the talent + admin dashboards that
 * surfaces TIME-CRITICAL action items (sub-24h). Hidden when no items.
 *
 * Talent items: confirm shift, clock in, standby/backup activated,
 *               urgent compliance block.
 * Admin items:  unconfirmed talent, missing clock-ins, standby ready,
 *               no-show risks, unfilled within 24h.
 *
 * Polls every 60s while mounted so it stays current without page refresh.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertOctagon, Clock, Zap, ChevronRight, ShieldAlert, Users, X } from 'lucide-react';
import api from '../utils/api';

const KIND_ICON = {
  confirm_shift: AlertOctagon,
  clock_in_missing: Clock,
  standby_activated: Zap,
  backup_activated: Zap,
  account_inactive: ShieldAlert,
  agreement_unsigned: ShieldAlert,
  unconfirmed_talent: AlertOctagon,
  missing_clock_ins: Clock,
  standby_ready: Zap,
  no_show_risk: ShieldAlert,
  unfilled_24h: Users,
};

const KIND_TONE = {
  // Tones map to bg/border. Reds for hardest blockers, ambers for warnings,
  // emeralds for positive activations.
  confirm_shift: 'amber',
  clock_in_missing: 'red',
  standby_activated: 'emerald',
  backup_activated: 'emerald',
  account_inactive: 'red',
  agreement_unsigned: 'red',
  unconfirmed_talent: 'amber',
  missing_clock_ins: 'red',
  standby_ready: 'emerald',
  no_show_risk: 'red',
  unfilled_24h: 'amber',
};

const TONE_STYLES = {
  red:     { wrap: 'bg-rose-50 border-rose-200',     icon: 'text-rose-600',     btn: 'bg-rose-600 hover:bg-rose-700 text-white' },
  amber:   { wrap: 'bg-amber-50 border-amber-200',   icon: 'text-amber-600',    btn: 'bg-amber-600 hover:bg-amber-700 text-white' },
  emerald: { wrap: 'bg-emerald-50 border-emerald-200', icon: 'text-emerald-600', btn: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
};

export default function Lock24hBanner({ pollMs = 60000 }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => {
    try { return sessionStorage.getItem('lock24h_collapsed') === '1'; }
    catch { return false; }
  });

  const fetchItems = useCallback(async () => {
    try {
      const res = await api.get('/urgent/next-24h');
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch {
      // Silent failure — banner just stays hidden.
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    const t = setInterval(fetchItems, pollMs);
    return () => clearInterval(t);
  }, [fetchItems, pollMs]);

  if (loading || items.length === 0) return null;

  // Strongest tone wins for the overall border + header chip.
  const tone = items.some((i) => KIND_TONE[i.kind] === 'red')
    ? 'red'
    : items.some((i) => KIND_TONE[i.kind] === 'amber')
      ? 'amber'
      : 'emerald';
  const styles = TONE_STYLES[tone];

  if (collapsed) {
    // Compact pill-style toggle when user has collapsed the banner.
    return (
      <button
        type="button"
        onClick={() => {
          setCollapsed(false);
          try { sessionStorage.removeItem('lock24h_collapsed'); } catch { /* ignore */ }
        }}
        className={`sticky top-0 z-40 w-full px-4 py-2 border-b ${styles.wrap} flex items-center justify-center gap-2 text-sm font-medium hover:brightness-95 transition`}
        data-testid="lock24h-collapsed-toggle"
      >
        <AlertOctagon className={`w-4 h-4 ${styles.icon}`} />
        <span>{items.length} urgent action{items.length === 1 ? '' : 's'} in next 24h</span>
        <ChevronRight className="w-4 h-4 rotate-90" />
      </button>
    );
  }

  return (
    <div
      className={`sticky top-0 z-40 border-b ${styles.wrap} shadow-sm`}
      data-testid="lock24h-banner"
      role="region"
      aria-label="Urgent actions in the next 24 hours"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <AlertOctagon className={`w-4 h-4 ${styles.icon}`} />
            <span>Lock in next 24h</span>
            <span
              className={`inline-flex items-center justify-center rounded-full text-[10px] font-bold px-1.5 min-w-[20px] h-5 ${styles.btn}`}
              data-testid="lock24h-count"
            >
              {items.length}
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              setCollapsed(true);
              try { sessionStorage.setItem('lock24h_collapsed', '1'); } catch { /* ignore */ }
            }}
            className="text-gray-500 hover:text-gray-900 p-0.5 rounded"
            aria-label="Collapse urgent actions banner"
            data-testid="lock24h-collapse"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Horizontal scroll list — keeps the banner tight on mobile and
            never balloons the dashboard vertical space. */}
        <div
          className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory"
          data-testid="lock24h-list"
        >
          {items.map((it, idx) => {
            const Icon = KIND_ICON[it.kind] || AlertOctagon;
            const itTone = KIND_TONE[it.kind] || tone;
            const itStyles = TONE_STYLES[itTone];
            return (
              <div
                key={`${it.kind}-${idx}`}
                className={`flex-shrink-0 snap-start min-w-[260px] sm:min-w-[300px] bg-white rounded-lg border ${itStyles.wrap.replace('bg-', 'border-').replace('-50', '-200')} px-3 py-2 flex items-start gap-2`}
                data-testid={`lock24h-item-${it.kind}-${idx}`}
              >
                <div className={`mt-0.5 flex-shrink-0 ${itStyles.icon}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate" title={it.title}>
                    {it.title}
                  </div>
                  <div className="text-[11px] text-gray-600 leading-tight line-clamp-2">
                    {it.subtitle}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => it.action_path && navigate(it.action_path)}
                  className={`text-[11px] font-semibold rounded-full px-2.5 py-1 transition flex-shrink-0 ${itStyles.btn}`}
                  data-testid={`lock24h-action-${it.kind}-${idx}`}
                >
                  {it.action_label}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
